
























import { CourseDetails } from '@/jbi-shared/dto/jtms.dtos';
import { CourseModuleStatus } from '@/jbi-shared/types/jtms.types';
import { Debounce } from '@/jbi-shared/util/debounce.vue-decorator';
import {
  CourseModuleComponentItem,
  CreateCourseModulesApiDTO,
  defaultCourseModule,
  DeleteCourseModulesApiDTO,
  IndexBooleanPayload,
  UpdateCourseModuleApiDTO
} from '@/store/modules/jtms/course-module/course-module.types';
import { RootState } from '@/store/store';
import { ApiState } from '@/store/types/general.types';
import FieldWrapper from '@/views/AdminGroupSettings/components/GroupLevelAttributes/FieldWrapper.vue';
import { ToastProgrammatic as Toast } from 'buefy';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Action, State } from 'vuex-class';
import CourseModuleComponent from './components/CourseModuleComponent.vue';

@Component({
  components: {
    FieldWrapper,
    CourseModuleComponent
  }
})
export default class CourseModulesTab extends Vue {
  @Prop(Boolean) isModuleTabOpened!: boolean;
  @Prop({ default: false }) isSettings!: boolean;

  /** API calls */
  @Action('courseModule/createCourseModuleApi')
  createCourseModuleApi!: (payload: CreateCourseModulesApiDTO) => void;

  @Action('courseModule/updateCourseModuleApi')
  updateCourseModuleApi!: (payload: UpdateCourseModuleApiDTO) => void;

  @Action('courseModule/deleteCourseModuleApi')
  deleteCourseModuleApi!: (payload: DeleteCourseModulesApiDTO) => void;

  @State(
    (state: RootState) => state.courseModule.apiState.createCourseModulesApi
  )
  createCourseModuleApiState!: ApiState;

  @State(
    (state: RootState) => state.courseModule.apiState.updateCourseModulesApi
  )
  updateCourseModuleApiState!: ApiState;

  @State(
    (state: RootState) => state.courseModule.apiState.deleteCourseModulesApi
  )
  deleteCourseModuleApiState!: ApiState;

  /** State related calls */
  @Action('courseModule/clearDeletedCourseModules')
  clearDeletedCourseModules!: () => void;

  @Action('courseModule/addActiveCourseModule')
  addActiveCourseModule!: (payload: CourseModuleComponentItem) => void;

  @Action('courseModule/removeActiveCourseModule')
  removeActiveCourseModule!: (index: number) => void;

  @Action('courseModule/addDeletedCourseModule')
  addDeletedCourseModule!: (payload: CourseModuleComponentItem) => void;

  @Action('courseModule/updateCourseModuleFormError')
  updateCourseModuleFormError!: (payload: boolean) => void;

  @Action('courseModule/updateCourseModulePrerequisiteDropdownState')
  updateCourseModulePrerequisiteDropdownState!: (
    payload: IndexBooleanPayload
  ) => void;

  @State((state: RootState) => state.course.courseDetails)
  courseDetails!: CourseDetails;

  @State(
    (state: RootState) =>
      state.courseModule.courseModulesTabState.activeCourseModules
  )
  activeCourseModules!: CourseModuleComponentItem[];

  @State((state: RootState) => state.course.courseCreationState.hasModules)
  hasModules!: boolean;

  get courseId() {
    return Number(this.courseDetails.id);
  }

  get hasDefaultCourseModule() {
    if (!this.activeCourseModules) {
      return false;
    }

    return this.activeCourseModules.some(
      (cm) => cm.status === CourseModuleStatus.DEFAULT
    );
  }

  created() {
    this.handleCourseComponentSetup();
  }

  @Watch('isModuleTabOpened')
  handleCourseComponentSetup() {
    // Only perform course package related changes when the tab is open
    if (this.isModuleTabOpened) {
      // Only add course pacakges if no course packages found
      if (this.activeCourseModules.length === 0) {
        this.addCourseModules();
      }

      /**
       * Default course modules are assigned into the UI as first initial course package component.
       * As such, the component will already have existing ID, and can directly call the create endpoint.
       */
      if (
        this.activeCourseModules.length === 1 &&
        this.hasDefaultCourseModule
      ) {
        this.createCourseModuleApi(this.constructCreatePayload());
      }
    }
  }

  constructCreatePayload(tempId?: number): CreateCourseModulesApiDTO {
    if (tempId) {
      return {
        courseId: Number(this.courseDetails.id),
        courseModuleTempId: tempId
      };
    }
    // If no tempId was provided, create payload for upserting default course modules
    return {
      courseId: Number(this.courseDetails.id),
      courseModuleTempId: this.activeCourseModules[0].id!
    };
  }

  // Adds default course module values for dynamically rendered components
  addCourseModules() {
    const newCourseModule = defaultCourseModule();
    this.addActiveCourseModule({
      ...newCourseModule
    });

    // Add API call
    this.createCourseModuleApi(
      this.constructCreatePayload(newCourseModule.tempId!)
    );
  }

  removeCourseModule(indexToRemove: number) {
    const courseModuleToDelete: CourseModuleComponentItem = this
      .activeCourseModules[indexToRemove];
    this.removeActiveCourseModule(indexToRemove);
    // this.addDeletedCourseModule(courseModuleToDelete);
    this.deleteCourseModuleApi({
      courseId: this.courseId,
      courseModuleIdsToDelete: [Number(courseModuleToDelete.id)]
    });
  }

  @Debounce(1500)
  handleUpdateCourseModules(indexToUpdate: number) {
    // Retrieve active course package from state
    const courseModuleToUpdate = this.activeCourseModules[indexToUpdate];

    const payload: UpdateCourseModuleApiDTO = {
      courseId: Number(this.courseDetails.id),
      courseModulesToUpdate: [
        {
          id: courseModuleToUpdate.id!,
          moduleId: courseModuleToUpdate.module
            ? courseModuleToUpdate.module.id
            : undefined,
          startDate: courseModuleToUpdate.startDate!,
          endDate: courseModuleToUpdate.endDate!,
          note: courseModuleToUpdate.note,
          trainerIds: courseModuleToUpdate.trainers
            ? courseModuleToUpdate.trainers.map((trainer) => trainer.id)
            : [],
          prerequisiteIds: courseModuleToUpdate.prerequisiteIds
            ? courseModuleToUpdate.prerequisiteIds.map((prereq) => prereq.id)
            : []
        }
      ]
    };

    this.updateCourseModuleApi(payload);
  }

  @Watch('activeCourseModules', { deep: true })
  validateOverallFormErrorState() {
    // Assume there is error because course modules tab has not been visited
    if (this.activeCourseModules.length === 1 && !this.isModuleTabOpened) {
      this.updateCourseModuleFormError(true);
    }
  }

  @Watch('updateCourseModuleApiState', { deep: true })
  onApiStateChanged(state: ApiState) {
    if (state.success) {
      Toast.open({
        type: 'is-success',
        position: 'is-top',
        message: 'Saved',
        queue: true,
        duration: 1000
      });
    }

    if (state.loading) {
      Toast.open({
        type: 'is-dark',
        position: 'is-top',
        message: 'Saving...',
        queue: true,
        duration: 800
      });
    }

    if (state.error) {
      Toast.open({
        queue: true,
        type: 'is-danger',
        position: 'is-top',
        message: 'Something went wrong. Could not process course modules.'
      });
    }
  }
}
